import { generatorById, STAGE_STATE_LABEL, findShot, type Continuity, type MusicTrack, type Shot, type ShotType, type TransitionType } from '@ai-studio/shared';

import { addCue, addLine, generatorChoices, nextId, pieceState, updateCue, updateLine, updateShot } from '../../lib/video.js';
import { currentDecider, useGeneration } from '../../state/generation.js';
import { useVideo } from '../../state/video.js';
import { useWorkspace } from '../../state/workspace.js';
import { RequestCard } from '../generation/GenerationInbox.js';
import { Card, IconButton, JsonField, NumberField, PropRow, SelectField, TextField, Toggle } from '../ui/controls.js';
import { GeneratorTag } from '../ui/GeneratorTag.js';
import { Icon } from '../ui/icons.js';
import { TextArea } from './StoryPanel.js';

/** One piece (keyframe, clip, voice...): its state, request card (approve / review here) and regenerate. */
function PieceRow({ label, pieceKey, group, shot }: { label: string; pieceKey: string; group: string; shot?: string }) {
  const projectId = useWorkspace((s) => s.projectId);
  const piece = useVideo((s) => s.pieces[pieceKey]);
  const request = useGeneration((s) => (piece?.request ? s.requests.find((r) => r.id === piece.request) : undefined));
  const state = piece?.state ?? 'none';
  const s = useVideo.getState();
  return (
    <div className="vs-piece">
      <div className="vs-row">
        <span className={`vs-dot is-${state}`} />
        <span>{label}</span>
        <span className="muted">{STAGE_STATE_LABEL[state]}</span>
        <span className="bar-spacer" />
        <button type="button" className="link-btn" onClick={() => void s.render({ groups: [group as never], ...(shot ? { shot } : {}), keys: [pieceKey] })} title="Make this piece (again) with the current settings">
          {state === 'none' ? 'Make' : 'Regenerate'}
        </button>
      </div>
      {request && projectId !== null && ['awaiting-approval', 'review', 'running', 'queued', 'failed'].includes(request.status) && (
        <RequestCard request={request} projectId={projectId} readOnly={currentDecider() !== 'human'} />
      )}
    </div>
  );
}

function GeneratorPick({ stage, value, onChange }: { stage: 'animatic' | 'final'; value: string; onChange: (v: string | undefined) => void }) {
  const g = generatorById(value);
  return (
    <div className="vs-gen">
      <SelectField value={value} options={generatorChoices(stage)} onChange={(v) => onChange(v)} />
      {g && <GeneratorTag gen={g} time />}
    </div>
  );
}

function MusicPanel() {
  const doc = useVideo((s) => s.doc)!;
  const s = useVideo.getState();
  const total = s.timeline().total;
  const set = (id: string, patch: Partial<MusicTrack>) => s.edit((d) => ({ ...d, audio: { ...d.audio, music: d.audio.music.map((m) => (m.id === id ? { ...m, ...patch } : m)) } }));
  return (
    <Card
      title="Music"
      icon="audio"
      subtitle={String(doc.audio.music.length)}
      actions={<IconButton icon="plus" size={13} title="Add a music track" onClick={() => s.edit((d) => ({ ...d, audio: { ...d.audio, music: [...d.audio.music, { id: nextId('m', d.audio.music.map((m) => m.id)), prompt: '', lyrics: '[instrumental]', start: 0, end: Math.round(total) }] } }))} />}
    >
      {doc.audio.music.map((m) => (
        <div key={m.id} className="vs-loc">
          <div className="vs-row">
            <code>{m.id}</code>
            <span className="bar-spacer" />
            <PieceRowInline pieceKey={`music:${m.id}`} />
            <IconButton icon="trash" size={12} title="Remove track" onClick={() => s.edit((d) => ({ ...d, audio: { ...d.audio, music: d.audio.music.filter((x) => x.id !== m.id) } }))} />
          </div>
          <PropRow label="Style">
            <TextField value={m.prompt} placeholder="epic orchestral, choir, 90 bpm" onChange={(prompt) => set(m.id, { prompt })} />
          </PropRow>
          <span className="muted vs-label">Lyrics ([verse] / [chorus] or [instrumental])</span>
          <TextArea rows={3} value={m.lyrics} onChange={(lyrics) => set(m.id, { lyrics })} />
          <div className="vs-row">
            <span className="muted">From</span>
            <NumberField value={m.start} min={0} step={1} onChange={(start) => set(m.id, { start })} />
            <span className="muted">to</span>
            <NumberField value={m.end ?? total} min={0} step={1} onChange={(end) => set(m.id, { end })} />
            <Toggle checked={m.duck !== false} label="Duck" onChange={(duck) => set(m.id, { duck })} />
          </div>
        </div>
      ))}
      <PropRow label="Music level">
        <NumberField value={doc.audio.musicVolume} min={0} max={2} step={0.05} onChange={(musicVolume) => s.edit((d) => ({ ...d, audio: { ...d.audio, musicVolume } }))} />
      </PropRow>
      <PropRow label="Loudness (LUFS)">
        <NumberField value={doc.audio.loudness} min={-30} max={-8} step={1} onChange={(loudness) => s.edit((d) => ({ ...d, audio: { ...d.audio, loudness } }))} />
      </PropRow>
    </Card>
  );
}

function PieceRowInline({ pieceKey }: { pieceKey: string }) {
  const state = useVideo((s) => pieceState(s.pieces, pieceKey));
  return <span className={`vs-badge is-${state}`}>{STAGE_STATE_LABEL[state]}</span>;
}

function RenderSettings() {
  const doc = useVideo((s) => s.doc)!;
  const s = useVideo.getState();
  const r = doc.render;
  const set = (patch: Partial<typeof r>) => s.edit((d) => ({ ...d, render: { ...d.render, ...patch } }));
  return (
    <Card title="Render settings" icon="settings" defaultOpen={false}>
      <PropRow label="Animatic model">
        <GeneratorPick stage="animatic" value={r.animatic} onChange={(v) => set({ animatic: v ?? 'animatic' })} />
      </PropRow>
      <PropRow label="Final model">
        <GeneratorPick stage="final" value={r.final} onChange={(v) => set({ final: v ?? 'shot' })} />
      </PropRow>
      <PropRow label="Re-anchor every" hint="Chained shots start from the previous shot's last frame; every N shots a fresh keyframe stops faces and colors from drifting.">
        <NumberField value={r.reanchorEvery} min={1} max={12} step={1} integer onChange={(reanchorEvery) => set({ reanchorEvery })} />
      </PropRow>
      <PropRow label="Keyframes">
        <SelectField value={r.keyframeStyle} options={[{ value: 'photo-fast', label: 'Photoreal fast (RealVisXL Lightning)' }, { value: 'photo', label: 'Photoreal best (RealVisXL, slow)' }, { value: 'illustration', label: 'Illustration (SDXL)' }]} onChange={(v) => set({ keyframeStyle: v as typeof r.keyframeStyle })} />
      </PropRow>
      <PropRow label="Interpolate">
        <Toggle checked={r.interpolate} onChange={(interpolate) => set({ interpolate })} label="RIFE for 16 fps clips" />
      </PropRow>
      <PropRow label="Upscale">
        <SelectField value={r.upscale} options={[{ value: 'none', label: 'None' }, { value: 'fast', label: 'Real-ESRGAN (fast)' }, { value: 'seedvr2', label: 'SeedVR2 (best, slow)' }]} onChange={(v) => set({ upscale: v as typeof r.upscale })} />
      </PropRow>
      <PropRow label="Subtitles">
        <Toggle checked={r.subtitles} onChange={(subtitles) => set({ subtitles })} />
      </PropRow>
      <PropRow label="Draft clips in cut" hint="Use animatic clips where finals are missing when assembling">
        <Toggle checked={r.allowDraftClips === true} onChange={(allowDraftClips) => set({ allowDraftClips })} />
      </PropRow>
      <PropRow label="Engine scene">
        <TextField value={doc.engine?.scene} placeholder="scenes/main.scene.json" mono onChange={(scene) => s.edit((d) => ({ ...d, engine: { ...(d.engine ?? {}), scene: scene || undefined } }))} />
      </PropRow>
    </Card>
  );
}

export function ShotInspector() {
  const doc = useVideo((s) => s.doc);
  const selection = useVideo((s) => s.selection);
  if (!doc) return null;
  const s = useVideo.getState();
  const ref = selection.kind === 'shot' ? findShot(doc, selection.id) : undefined;
  if (!ref) {
    return (
      <div className="inspector vs-insp">
        <p className="muted">Select a shot in the storyboard to edit it, see its pieces and approve or review them here.</p>
        <PieceRow label="Final video (assembly)" pieceKey="output:assemble" group="assemble" />
        <MusicPanel />
        <RenderSettings />
      </div>
    );
  }
  const shot = ref.shot;
  const edit = (patch: Partial<Shot>) => s.edit((d) => updateShot(d, shot.id, patch));
  const cam = shot.camera ?? {};
  const chars = doc.characters;
  return (
    <div className="inspector vs-insp">
      <Card title={shot.title || shot.id} icon={shot.type === 'engine' ? 'scene' : 'camera'} subtitle={`${ref.scene.title} · ${shot.duration}s`}>
        <PropRow label="Title">
          <TextField value={shot.title} onChange={(title) => edit({ title: title || undefined })} />
        </PropRow>
        <PropRow label="Type">
          <SelectField value={shot.type} options={[{ value: 'ai', label: 'AI video' }, { value: 'engine', label: 'Engine (sequence)' }]} onChange={(type) => edit({ type: type as ShotType })} />
        </PropRow>
        <PropRow label="Duration (s)">
          <NumberField value={shot.duration} min={1} max={120} step={0.5} onChange={(duration) => edit({ duration })} />
        </PropRow>
        <span className="muted vs-label">What happens (story beat)</span>
        <TextArea value={shot.description} onChange={(description) => edit({ description })} />
        {shot.type === 'ai' && (
          <>
            <span className="muted vs-label">Visual prompt (empty: built from the description, characters, location and style)</span>
            <TextArea rows={2} value={shot.prompt} onChange={(prompt) => edit({ prompt: prompt || undefined })} />
            <PropRow label="Avoid">
              <TextField value={shot.negative} onChange={(negative) => edit({ negative: negative || undefined })} />
            </PropRow>
          </>
        )}
        <span className="muted vs-label">How it ends (carried into the next shot)</span>
        <TextArea rows={2} value={shot.summary} onChange={(summary) => edit({ summary: summary || undefined })} />
      </Card>

      <Card title="Camera" icon="camera">
        <PropRow label="Framing">
          <TextField value={cam.framing} placeholder="wide, medium close-up…" onChange={(framing) => edit({ camera: { ...cam, framing: framing || undefined } })} />
        </PropRow>
        <PropRow label="Movement">
          <TextField value={cam.movement} placeholder="slow dolly in, static…" onChange={(movement) => edit({ camera: { ...cam, movement: movement || undefined } })} />
        </PropRow>
        <PropRow label="Lens">
          <TextField value={cam.lens} placeholder="35mm, anamorphic…" onChange={(lens) => edit({ camera: { ...cam, lens: lens || undefined } })} />
        </PropRow>
        {shot.type === 'engine' && (
          <>
            <PropRow label="Camera entity" hint="Entity id of a camera in the scene">
              <TextField value={cam.entity} mono onChange={(entity) => edit({ camera: { ...cam, entity: entity || undefined } })} />
            </PropRow>
            <PropRow label="Look at">
              <TextField value={cam.lookAt} mono onChange={(lookAt) => edit({ camera: { ...cam, lookAt: lookAt || undefined } })} />
            </PropRow>
            <span className="muted vs-label">Camera keys (shot time): position / rotation / fov</span>
            <JsonField value={{ position: cam.position ?? [], rotation: cam.rotation ?? [], fov: cam.fov ?? [] }} onChange={(v) => edit({ camera: { ...cam, ...(v as object) } })} />
            <span className="muted vs-label">Animations [{'{target, clip, start, end, loop, blendIn}'}]</span>
            <JsonField value={shot.engine?.animations ?? []} onChange={(animations) => edit({ engine: { ...(shot.engine ?? {}), animations: animations as never } })} />
            <PropRow label="Scene">
              <TextField value={shot.engine?.scene} placeholder={doc.engine?.scene ?? 'scenes/main.scene.json'} mono onChange={(scene) => edit({ engine: { ...(shot.engine ?? {}), scene: scene || undefined } })} />
            </PropRow>
          </>
        )}
      </Card>

      <Card title="Cast and place" icon="team">
        <div className="vs-chips">
          {chars.map((c) => {
            const on = (shot.characters ?? []).includes(c.id);
            return (
              <button key={c.id} type="button" className={`vs-chip${on ? ' is-on' : ''}`} onClick={() => edit({ characters: on ? (shot.characters ?? []).filter((x) => x !== c.id) : [...(shot.characters ?? []), c.id] })}>
                {c.name}
              </button>
            );
          })}
        </div>
        <PropRow label="Location">
          <SelectField value={shot.location ?? ''} options={[{ value: '', label: '(scene location)' }, ...doc.locations.map((l) => ({ value: l.id, label: l.name }))]} onChange={(v) => edit({ location: v || undefined })} />
        </PropRow>
        {shot.type === 'ai' && (
          <PropRow label="Continuity" hint="previous-last-frame chains from the previous shot (re-anchored every few shots); keyframe starts fresh; first-last bridges two keyframes">
            <SelectField value={shot.continuity ?? 'previous-last-frame'} options={['previous-last-frame', 'keyframe', 'first-last']} onChange={(v) => edit({ continuity: v as Continuity })} />
          </PropRow>
        )}
        <PropRow label="Transition in">
          <div className="vs-row">
            <SelectField value={shot.transition?.type ?? 'cut'} options={['cut', 'crossfade', 'fade-black']} onChange={(v) => edit({ transition: { type: v as TransitionType, duration: shot.transition?.duration ?? 0.5 } })} />
            {shot.transition && shot.transition.type !== 'cut' && <NumberField value={shot.transition.duration ?? 0.5} min={0.1} max={4} step={0.1} onChange={(duration) => edit({ transition: { ...shot.transition!, duration } })} />}
          </div>
        </PropRow>
      </Card>

      <Card
        title="Dialogue"
        icon="audio"
        subtitle={String(shot.dialogue?.length ?? 0)}
        actions={<IconButton icon="plus" size={13} title="Add a line" onClick={() => s.edit((d) => addLine(d, shot.id))} />}
      >
        {(shot.dialogue ?? []).map((l) => (
          <div key={l.id} className="vs-line">
            <div className="vs-row">
              <SelectField value={l.character} options={chars.map((c) => ({ value: c.id, label: c.name }))} onChange={(character) => s.edit((d) => updateLine(d, shot.id, l.id, { character }))} />
              <TextField value={l.emotion} placeholder="emotion" onChange={(emotion) => s.edit((d) => updateLine(d, shot.id, l.id, { emotion: emotion || undefined }))} />
              <IconButton icon="trash" size={12} title="Remove line" onClick={() => s.edit((d) => updateLine(d, shot.id, l.id, null))} />
            </div>
            <TextArea rows={2} value={l.text} onChange={(text) => s.edit((d) => updateLine(d, shot.id, l.id, { text }))} placeholder="What they say" />
            <div className="vs-row">
              <span className="muted">At (s)</span>
              <NumberField value={l.offset} min={0} max={shot.duration} step={0.1} onChange={(offset) => s.edit((d) => updateLine(d, shot.id, l.id, { offset }))} />
              <Toggle checked={l.onCamera !== false} label="Face on camera (lip sync)" onChange={(onCamera) => s.edit((d) => updateLine(d, shot.id, l.id, { onCamera }))} />
            </div>
            <PieceRow label="Voice" pieceKey={`line:${shot.id}:${l.id}:voice`} group="voice" shot={shot.id} />
            {shot.type === 'ai' && l.onCamera !== false && <PieceRow label="Lip sync" pieceKey={`line:${shot.id}:${l.id}:lipsync`} group="lipsync" shot={shot.id} />}
          </div>
        ))}
      </Card>

      <Card title="Sound effects" icon="audio" subtitle={String(shot.sfx?.length ?? 0)} defaultOpen={Boolean(shot.sfx?.length)} actions={<IconButton icon="plus" size={13} title="Add a sound cue" onClick={() => s.edit((d) => addCue(d, shot.id))} />}>
        {(shot.sfx ?? []).map((c) => (
          <div key={c.id} className="vs-line">
            <div className="vs-row">
              <TextField value={c.prompt} placeholder="armor clank, close" onChange={(prompt) => s.edit((d) => updateCue(d, shot.id, c.id, { prompt }))} />
              <IconButton icon="trash" size={12} title="Remove cue" onClick={() => s.edit((d) => updateCue(d, shot.id, c.id, null))} />
            </div>
            <div className="vs-row">
              <span className="muted">At</span>
              <NumberField value={c.offset ?? 0} min={0} max={shot.duration} step={0.1} onChange={(offset) => s.edit((d) => updateCue(d, shot.id, c.id, { offset }))} />
              <span className="muted">Length</span>
              <NumberField value={c.duration ?? 4} min={0.5} max={47} step={0.5} onChange={(duration) => s.edit((d) => updateCue(d, shot.id, c.id, { duration }))} />
            </div>
            <PieceRow label="Sound" pieceKey={`sfx:${shot.id}:${c.id}`} group="sfx" shot={shot.id} />
          </div>
        ))}
      </Card>

      <Card title="Pieces and review" icon="sparkles">
        {shot.type === 'engine' ? (
          <PieceRow label="Engine recording (prism record)" pieceKey={`shot:${shot.id}:engine`} group="engine" shot={shot.id} />
        ) : (
          <>
            <PieceRow label="Keyframe" pieceKey={`shot:${shot.id}:keyframe`} group="keyframes" shot={shot.id} />
            {shot.continuity === 'first-last' && <PieceRow label="End keyframe" pieceKey={`shot:${shot.id}:endKeyframe`} group="keyframes" shot={shot.id} />}
            <PieceRow label="Animatic" pieceKey={`shot:${shot.id}:animatic`} group="animatic" shot={shot.id} />
            <PieceRow label="Final" pieceKey={`shot:${shot.id}:final`} group="final" shot={shot.id} />
            <PieceRow label="Post" pieceKey={`shot:${shot.id}:post`} group="post" shot={shot.id} />
          </>
        )}
        {shot.type === 'ai' && (
          <>
            <PropRow label="Animatic model">
              <GeneratorPick stage="animatic" value={shot.generators?.animatic ?? doc.render.animatic} onChange={(animatic) => edit({ generators: { ...(shot.generators ?? {}), animatic } })} />
            </PropRow>
            <PropRow label="Final model">
              <GeneratorPick stage="final" value={shot.generators?.final ?? doc.render.final} onChange={(final) => edit({ generators: { ...(shot.generators ?? {}), final } })} />
            </PropRow>
          </>
        )}
        <p className="muted vs-hint">
          <Icon name="info" size={11} /> Approvals and reviews also appear in the Generations inbox. Refine a clip to extend, interpolate or upscale it, or to make a new take with your notes.
        </p>
      </Card>
    </div>
  );
}
